import React, { useRef, useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Splide, SplideSlide } from '@splidejs/react-splide';
import '@splidejs/react-splide/css';
import api from "../../../api/axois";
import InformationCard from "../../../components/User/InformationCard";
import ReviewsSection from "../../../components/Reviews/ReviewsSection";
import styles from "./OneBedroomPage.module.css";
import { getImageUrl } from "../../../utils/imageUtils";

const defaultSlides = ["/images/slide1.jpg", "/images/slide2.jpg", "/images/slide3.jpg"];

const OneBedroomPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const cardRef = useRef(null);
  const stopRef = useRef(null);
  const splideRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSliderHovered, setIsSliderHovered] = useState(false);

  // Booking form state
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [airportPickup, setAirportPickup] = useState(false);
  const [flightNumber, setFlightNumber] = useState("");
  const [bookingSuccess, setBookingSuccess] = useState("");
  const [bookingError, setBookingError] = useState("");
  const [redirecting, setRedirecting] = useState(false);

  // Handle affiliate code preservation
  useEffect(() => {
    try {
      const sp = new URLSearchParams(location.search || "");
      const code = sp.get("aff") || sp.get("affiliate") || sp.get("ref") || sp.get("code");
      if (code) {
        // Store the affiliate code so InformationCard can access it
        localStorage.setItem("affiliate_code", code);
      }
    } catch {}
  }, [location.search]);

  useEffect(() => {
    const fetchRoom = async () => {
      try {
        const res = await api.get(`/rooms/${id}`);
        setRoom(res.data.room);
      } catch {
        setRoom(null);
      } finally {
        setLoading(false);
      }
    };
    fetchRoom();
  }, [id]);

  // Update Splide options when hover state changes (for desktop)
  useEffect(() => {
    if (splideRef.current && window.innerWidth > 1080) {
      const splide = splideRef.current.splide;
      if (splide) {
        splide.options.drag = isSliderHovered;
        splide.options.wheel = isSliderHovered;
      }
    }
  }, [isSliderHovered]);

  // Handle splide events
  const handleSplideMove = (splide, newIndex) => {
    setActiveIndex(newIndex);
  };

  if (loading) return <div>Loading room details...</div>;
  if (!room) return <div>Room not found.</div>;


  // Support both RoomType and Room structures
  const getImages = () => {
    if (room.images && Array.isArray(room.images) && room.images.length > 0) return room.images;
    if (room.roomType && Array.isArray(room.roomType.images) && room.roomType.images.length > 0) return room.roomType.images;
    return defaultSlides;
  };
  const getPrice = () => {
    if (room.price_per_night) return room.price_per_night;
    if (room.roomType && room.roomType.price_per_night) return room.roomType.price_per_night;
    return null;
  };

  const slides = getImages();
  const price = getPrice();

  // Extract latitude and longitude from room or roomType
  let latitude = null;
  let longitude = null;
  if (room) {
    if (typeof room.latitude !== 'undefined' && room.latitude !== null) {
      latitude = Number(room.latitude);
    } else if (room.roomType && typeof room.roomType.latitude !== 'undefined') {
      latitude = Number(room.roomType.latitude);
    }
    if (typeof room.longitude !== 'undefined' && room.longitude !== null) {
      longitude = Number(room.longitude);
    } else if (room.roomType && typeof room.roomType.longitude !== 'undefined') {
      longitude = Number(room.roomType.longitude);
    }
  }
  // Debug log



  return (
  <div>
      {redirecting && (
        <div className="redirect-modal">
          <div className="redirect-modal-content">
            <h3>You are about to be redirected to a payment portal...</h3>
            <p>Please wait while we connect you to Paystack.</p>
          </div>
        </div>
      )}
  <div className={styles.room_layout}>
        {/* Desktop stacked images */}
        <div className={styles.room_images__desktop_images}>
          {slides.map((slide, idx) => {
            return <img key={idx} src={getImageUrl(slide)} alt={`Room view ${idx + 1}`} />;
          })}
        </div>

        {/* Mobile slider */}
        <div 
          className={styles.room_images_mobile__mobile_slider}
          onMouseEnter={() => setIsSliderHovered(true)}
          onMouseLeave={() => setIsSliderHovered(false)}
        >
          <Splide
            ref={splideRef}
            options={{
              type: 'slide',
              perPage: 1,
              perMove: 1,
              gap: 0,
              arrows: false,
              pagination: false,
              drag: window.innerWidth <= 1080 ? true : isSliderHovered,
              wheel: window.innerWidth <= 1080 ? false : isSliderHovered,
              wheelSleep: 100,
              releaseWheel: true,
              fixedHeight: '450px',
              cover: true,
              breakpoints: {
                1080: {
                  drag: true,
                  wheel: false,
                  fixedHeight: '450px'
                }
              }
            }}
            onMove={handleSplideMove}
            style={{ 
              width: '100%',
              height: '450px'
            }}
          >
            {slides.map((slide, idx) => (
              <SplideSlide key={idx}>
                <img 
                  src={getImageUrl(slide)} 
                  alt={`Room view ${idx + 1}`} 
                  style={{ 
                    width: '100%', 
                    height: '450px',
                    objectFit: 'cover',
                    display: 'block' 
                  }}
                />
              </SplideSlide>
            ))}
          </Splide>
        </div>
        {/* Swiping dots indicator */}
        <div className={styles.slider_dots}>
          {slides.map((_, idx) => (
            <span
              key={idx}
              className={idx === activeIndex ? `${styles.dot} ${styles.dot_active}` : styles.dot}
              onClick={() => {
                if (splideRef.current) {
                  splideRef.current.go(idx);
                }
              }}
              style={{ cursor: 'pointer' }}
            />
          ))}
        </div>

  {/* Info card */}
  <div className={styles.room_card_wrapper} ref={cardRef}>
    <InformationCard display={{ ...room, images: slides, price_per_night: price, latitude, longitude }} />
  </div>
      </div>

      {/* Booking Form */}
     

      {/* Reviews section */}
  <div className={styles.room_reviews} ref={stopRef}>
    <ReviewsSection roomTypeId={room?.room_type_id || room?.roomType?.id} />
  </div>
    </div>
  );
};

export default OneBedroomPage;